/**
 * Bundle Tiered Discount Messaging
 * Displays dynamic discount messages based on bundle item count
 * Works with automatic discounts configured in Shopify admin
 */

(function() {
  'use strict';

  // Configuration - Adjust discount tiers based on your automatic discount setup
  // Note: The locked first product counts toward the total
  // After 4 products, discount stays at 15%
  const DISCOUNT_TIERS = [
    { minItems: 3, discountPercent: 10, message: '10% off applied at checkout' },
    { minItems: 4, discountPercent: 15, message: '15% off applied at checkout' }
  ];

  // Configuration for checkbox bundles (product information bundles)
  // No locked item, starts at 0 items
  const CHECKBOX_DISCOUNT_TIERS = [
    { minItems: 3, discountPercent: 10, message: '10% off applied at checkout' },
    { minItems: 4, discountPercent: 15, message: '15% off applied at checkout' }
  ];

  // Selectors - Update if your bundle builder uses different selectors
  const CONFIG = {
    bundleContainers: [
      'product-bundle', // Primary selector for the bundle custom element
      '[data-section-type="product-bundle"]', // Fallback selector
      '.product-bundle-wrapper' // Additional fallback
    ],
    checkboxBundleContainers: [
      'product-bundle-details', // Checkbox-based bundle in product info
      'details[is="product-bundle-details"]' // Fallback
    ],
    messageContainer: '.product-bundle__footer', // Where to display messages (full bundle)
    checkboxMessageContainer: '.details__content', // Where to display messages (checkbox bundle)
    variantSelector: '[data-product-bundle-variant]',
    priceSelector: '[data-product-bundle-variant-price]',
    checkboxSelector: 'input[name="bundles"]'
  };

  class BundleTieredDiscounts {
    constructor(bundleElement) {
      this.bundle = bundleElement;
      this.messageElement = null;
      this.incentiveElement = null;
      this.currentTier = null;
      this.bundleType = 'full'; // 'full' or 'checkbox'
      this.init();
    }

    init() {
      // Determine bundle type
      if (this.bundle.tagName === 'DETAILS' || this.bundle.hasAttribute('is') && this.bundle.getAttribute('is') === 'product-bundle-details') {
        this.bundleType = 'checkbox';
        this.initCheckboxBundle();
      } else {
        this.bundleType = 'full';
        this.initFullBundle();
      }
    }

    initFullBundle() {
      // Find or create message container
      const footer = this.bundle.querySelector(CONFIG.messageContainer);
      if (!footer) {
        console.warn('Bundle tiered discounts: Footer container not found');
        return;
      }

      // Create main discount message element if it doesn't exist
      this.messageElement = footer.querySelector('[data-bundle-discount-message]');
      if (!this.messageElement) {
        this.messageElement = document.createElement('div');
        this.messageElement.setAttribute('data-bundle-discount-message', '');
        this.messageElement.className = 'bundle-discount-message text-sm font-medium text-center';
        // Insert at the beginning of the footer, before the total section
        const firstChild = footer.firstElementChild;
        if (firstChild) {
          footer.insertBefore(this.messageElement, firstChild);
        } else {
          footer.appendChild(this.messageElement);
        }
      }

      // Create incentive message element if it doesn't exist (for showing "Add X more" above current discount)
      this.incentiveElement = footer.querySelector('[data-bundle-incentive-message]');
      if (!this.incentiveElement) {
        this.incentiveElement = document.createElement('div');
        this.incentiveElement.setAttribute('data-bundle-incentive-message', '');
        this.incentiveElement.className = 'bundle-incentive-message text-sm font-medium text-center';
        // Insert before the main discount message
        this.messageElement.parentNode.insertBefore(this.incentiveElement, this.messageElement);
      }

      // Listen for bundle changes
      this.bundle.addEventListener('bundle:added', this.updateMessage.bind(this));
      this.bundle.addEventListener('bundle:removed', this.updateMessage.bind(this));
      this.bundle.addEventListener('change', this.updateMessage.bind(this));

      // Initial update - use a small delay to ensure DOM is fully ready
      setTimeout(() => {
        this.updateMessage();
      }, 100);
    }

    initCheckboxBundle() {
      // Find or create message container (details content area)
      const content = this.bundle.querySelector(CONFIG.checkboxMessageContainer);
      if (!content) {
        console.warn('Bundle tiered discounts: Content container not found for checkbox bundle');
        return;
      }

      // Create main discount message element if it doesn't exist
      this.messageElement = content.querySelector('[data-bundle-discount-message]');
      if (!this.messageElement) {
        this.messageElement = document.createElement('div');
        this.messageElement.setAttribute('data-bundle-discount-message', '');
        this.messageElement.className = 'bundle-discount-message text-sm font-medium text-center mt-2';
        // Insert at the end of the content
        content.appendChild(this.messageElement);
      }

      // Create incentive message element if it doesn't exist
      this.incentiveElement = content.querySelector('[data-bundle-incentive-message]');
      if (!this.incentiveElement) {
        this.incentiveElement = document.createElement('div');
        this.incentiveElement.setAttribute('data-bundle-incentive-message', '');
        this.incentiveElement.className = 'bundle-incentive-message text-sm font-medium text-center mb-2';
        // Insert before the main discount message
        this.messageElement.parentNode.insertBefore(this.incentiveElement, this.messageElement);
      }

      // Listen for bundle changes (checkbox bundle uses 'bundle:change' event from product form)
      const productForm = this.bundle.getAttribute('form') ? document.forms[this.bundle.getAttribute('form')] : null;
      if (productForm) {
        this.bundleChangeListener = this.updateMessage.bind(this);
        (productForm || document).addEventListener('bundle:change', this.bundleChangeListener);
      }
      
      // Also listen to checkbox changes directly
      this.checkboxChangeListener = this.updateMessage.bind(this);
      this.bundle.addEventListener('change', this.checkboxChangeListener);

      // Initial update
      setTimeout(() => {
        this.updateMessage();
      }, 100);
    }

    /**
     * Count selected items in the bundle
     * Items are selected when they DON'T have the 'available' attribute
     * This includes locked products (which have data-locked but not available)
     * For checkbox bundles, counts checked checkboxes
     */
    countSelectedItems() {
      if (this.bundleType === 'checkbox') {
        // Count checked checkboxes
        const checkedBoxes = this.bundle.querySelectorAll(CONFIG.checkboxSelector + ':checked');
        return checkedBoxes.length;
      } else {
        // Count variants without 'available' attribute
        const variants = this.bundle.querySelectorAll(CONFIG.variantSelector);
        let count = 0;
        
        variants.forEach(variant => {
          // Selected variants don't have 'available' attribute
          // This includes both regular selected items and locked products
          if (!variant.hasAttribute('available')) {
            const quantityInput = variant.querySelector('quantity-input');
            if (quantityInput && quantityInput.input) {
              const qty = parseInt(quantityInput.input.value);
              count += (qty && qty > 0) ? qty : 1;
            } else {
              // If no quantity input, count as 1 (locked products or default quantity)
              count += 1;
            }
          }
        });

        return count;
      }
    }

    /**
     * Calculate total price of selected items
     */
    calculateTotalPrice() {
      if (this.bundleType === 'checkbox') {
        // Calculate from checked checkboxes
        const checkedBoxes = this.bundle.querySelectorAll(CONFIG.checkboxSelector + ':checked');
        let total = 0;
        
        checkedBoxes.forEach(checkbox => {
          const price = parseInt(checkbox.getAttribute('data-price')) || 0;
          total += price;
        });
        
        return total;
      } else {
        // Calculate from variants
        const variants = this.bundle.querySelectorAll(CONFIG.variantSelector);
        let total = 0;

        variants.forEach(variant => {
          if (!variant.hasAttribute('available')) {
            const priceElement = variant.querySelector(CONFIG.priceSelector);
            if (priceElement) {
              const price = parseInt(priceElement.getAttribute('data-price')) || 0;
              const quantityInput = variant.querySelector('quantity-input');
              const quantity = quantityInput && quantityInput.input 
                ? parseInt(quantityInput.input.value) || 1 
                : 1;
              total += price * quantity;
            }
          }
        });

        return total;
      }
    }

    /**
     * Get the current discount tier based on item count
     * After reaching the highest tier, it stays at that tier
     */
    getCurrentTier(itemCount) {
      const tiers = this.bundleType === 'checkbox' ? CHECKBOX_DISCOUNT_TIERS : DISCOUNT_TIERS;
      // Sort tiers by minItems descending to get the highest applicable tier
      const sortedTiers = [...tiers].sort((a, b) => b.minItems - a.minItems);
      
      // Get the highest tier first
      const highestTier = sortedTiers[0];
      
      // If item count exceeds the highest tier, return the highest tier (cap at max discount)
      if (itemCount >= highestTier.minItems) {
        return highestTier;
      }
      
      // Otherwise, find the applicable tier
      for (const tier of sortedTiers) {
        if (itemCount >= tier.minItems) {
          return tier;
        }
      }

      return null;
    }

    /**
     * Get the next tier the customer can unlock
     */
    getNextTier(itemCount) {
      const tiers = this.bundleType === 'checkbox' ? CHECKBOX_DISCOUNT_TIERS : DISCOUNT_TIERS;
      const sortedTiers = [...tiers].sort((a, b) => a.minItems - b.minItems);
      
      // Find the next tier the customer hasn't reached yet
      for (const tier of sortedTiers) {
        if (itemCount < tier.minItems) {
          return tier;
        }
      }

      return null;
    }

    /**
     * Calculate estimated savings
     */
    calculateSavings(totalPrice, discountPercent) {
      if (!discountPercent || !totalPrice) return 0;
      return Math.round((totalPrice * discountPercent) / 100);
    }

    /**
     * Format money using theme's currency formatter if available
     */
    formatMoney(cents) {
      if (window.theme && window.theme.Currency && window.theme.Currency.formatMoney) {
        const format = window.theme.settings && window.theme.settings.moneyFormat 
          ? window.theme.settings.moneyFormat 
          : '${{amount}}';
        return window.theme.Currency.formatMoney(cents, format);
      }
      
      // Fallback formatting
      return '$' + (cents / 100).toFixed(2);
    }

    /**
     * Update the discount message based on current bundle state
     */
    updateMessage() {
      if (!this.messageElement || !this.incentiveElement) return;

      const itemCount = this.countSelectedItems();
      const currentTier = this.getCurrentTier(itemCount);
      const nextTier = this.getNextTier(itemCount);
      const totalPrice = this.calculateTotalPrice();

      // Clear previous messages
      this.messageElement.innerHTML = '';
      this.incentiveElement.innerHTML = '';
      this.messageElement.className = 'bundle-discount-message text-sm font-medium text-center';
      this.incentiveElement.className = 'bundle-incentive-message text-sm font-medium text-center';

      // Handle checkbox bundles differently (no locked product, starts at 0)
      if (this.bundleType === 'checkbox') {
        // 0-2 products: Show "Add X products to get 10% discount"
        if (itemCount < 3) {
          this.incentiveElement.style.display = 'none';
          this.messageElement.style.display = 'block';
          this.messageElement.className += ' text-opacity';
          const productsNeeded = 3 - itemCount;
          this.messageElement.innerHTML = `Add ${productsNeeded} product${productsNeeded > 1 ? 's' : ''} to get 10% discount`;
          this.currentTier = null;
          return;
        }

        // 3 products: Show both incentive and current discount
        if (itemCount === 3 && currentTier && nextTier) {
          // Show incentive message above
          this.incentiveElement.style.display = 'block';
          this.incentiveElement.className += ' text-opacity mb-2';
          const productsNeeded = nextTier.minItems - itemCount;
          this.incentiveElement.innerHTML = `Add ${productsNeeded} more product${productsNeeded > 1 ? 's' : ''} to get ${nextTier.discountPercent}% discount`;
          
          // Show current discount message below
          this.messageElement.style.display = 'block';
          this.messageElement.className += ' alert alert--success';
          const savings = this.calculateSavings(totalPrice, currentTier.discountPercent);
          let message = currentTier.message;
          if (savings > 0) {
            message += ` <span class="font-bold">Save ${this.formatMoney(savings)}</span>`;
          }
          this.messageElement.innerHTML = message;
          this.currentTier = currentTier;
          return;
        }

        // 4+ products: Show current discount only
        if (currentTier) {
          this.incentiveElement.style.display = 'none';
          this.messageElement.style.display = 'block';
          this.messageElement.className += ' alert alert--success';
          
          const savings = this.calculateSavings(totalPrice, currentTier.discountPercent);
          let message = currentTier.message;
          if (savings > 0) {
            message += ` <span class="font-bold">Save ${this.formatMoney(savings)}</span>`;
          }
          
          this.messageElement.innerHTML = message;
          this.currentTier = currentTier;
          return;
        }

        // Fallback
        this.messageElement.style.display = 'none';
        this.incentiveElement.style.display = 'none';
        return;
      }

      // Full bundle logic (with locked product)
      // Always show message if there's at least 1 item (including locked product)
      // The locked product counts as 1 product
      if (itemCount === 0) {
        this.messageElement.style.display = 'none';
        this.incentiveElement.style.display = 'none';
        return;
      }

      // Special case: When at 3 products, show both incentive and current discount
      if (itemCount === 3 && currentTier && nextTier) {
        // Show incentive message above
        this.incentiveElement.style.display = 'block';
        this.incentiveElement.className += ' text-opacity mb-2';
        const productsNeeded = nextTier.minItems - itemCount;
        this.incentiveElement.innerHTML = `Add ${productsNeeded} more product${productsNeeded > 1 ? 's' : ''} to get ${nextTier.discountPercent}% discount`;
        
        // Show current discount message below
        this.messageElement.style.display = 'block';
        this.messageElement.className += ' alert alert--success';
        const savings = this.calculateSavings(totalPrice, currentTier.discountPercent);
        let message = currentTier.message;
        if (savings > 0) {
          message += ` <span class="font-bold">Save ${this.formatMoney(savings)}</span>`;
        }
        this.messageElement.innerHTML = message;
        this.currentTier = currentTier;
        return;
      }

      // If customer has reached a discount tier (4+ products)
      if (currentTier) {
        this.incentiveElement.style.display = 'none';
        this.messageElement.style.display = 'block';
        this.messageElement.className += ' alert alert--success';
        
        const savings = this.calculateSavings(totalPrice, currentTier.discountPercent);
        let message = currentTier.message;
        if (savings > 0) {
          message += ` <span class="font-bold">Save ${this.formatMoney(savings)}</span>`;
        }
        
        this.messageElement.innerHTML = message;
        this.currentTier = currentTier;
      }
      // If customer hasn't reached a tier yet (0-2 products), show next tier incentive
      else if (nextTier) {
        this.incentiveElement.style.display = 'none';
        this.messageElement.style.display = 'block';
        this.messageElement.className += ' text-opacity';
        
        const productsNeeded = nextTier.minItems - itemCount;
        // Use "discount" instead of "off" for incentive messages
        let message = `Add ${productsNeeded} product${productsNeeded > 1 ? 's' : ''} to get ${nextTier.discountPercent}% discount`;
        
        this.messageElement.innerHTML = message;
        this.currentTier = null;
      }
      // No discount tiers configured or product count below minimum
      else {
        this.messageElement.style.display = 'none';
        this.incentiveElement.style.display = 'none';
      }
    }

    /**
     * Cleanup
     */
    destroy() {
      if (this.messageElement) {
        this.messageElement.remove();
      }
      if (this.incentiveElement) {
        this.incentiveElement.remove();
      }
      
      if (this.bundleType === 'checkbox') {
        const productForm = this.bundle.getAttribute('form') ? document.forms[this.bundle.getAttribute('form')] : null;
        if (productForm && this.bundleChangeListener) {
          (productForm || document).removeEventListener('bundle:change', this.bundleChangeListener);
        }
        if (this.checkboxChangeListener) {
          this.bundle.removeEventListener('change', this.checkboxChangeListener);
        }
      } else {
        this.bundle.removeEventListener('bundle:added', this.updateMessage);
        this.bundle.removeEventListener('bundle:removed', this.updateMessage);
        this.bundle.removeEventListener('change', this.updateMessage);
      }
    }
  }

  // Initialize on page load
  function initBundleDiscounts() {
    // Wait for DOM and custom elements to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initBundleDiscounts);
      return;
    }

    // Find all bundle containers (both full and checkbox types)
    const bundles = [];
    
    // Initialize full bundle builders
    if (customElements.get('product-bundle')) {
      CONFIG.bundleContainers.forEach(selector => {
        const elements = document.querySelectorAll(selector);
        elements.forEach(el => {
          // Only add if it's a product-bundle custom element or contains one
          if (el.tagName === 'PRODUCT-BUNDLE' || el.querySelector('product-bundle')) {
            const bundleEl = el.tagName === 'PRODUCT-BUNDLE' ? el : el.querySelector('product-bundle');
            if (bundleEl && !bundleEl.bundleDiscountInstance) {
              try {
                bundleEl.bundleDiscountInstance = new BundleTieredDiscounts(bundleEl);
                bundles.push(bundleEl.bundleDiscountInstance);
              } catch (error) {
                console.warn('Bundle tiered discounts: Failed to initialize full bundle', error);
              }
            }
          }
        });
      });
    }
    
    // Initialize checkbox-based bundles (product-bundle-details)
    CONFIG.checkboxBundleContainers.forEach(selector => {
      const elements = document.querySelectorAll(selector);
      elements.forEach(el => {
        if (el.tagName === 'DETAILS' || (el.hasAttribute('is') && el.getAttribute('is') === 'product-bundle-details')) {
          if (!el.bundleDiscountInstance) {
            try {
              el.bundleDiscountInstance = new BundleTieredDiscounts(el);
              bundles.push(el.bundleDiscountInstance);
            } catch (error) {
              console.warn('Bundle tiered discounts: Failed to initialize checkbox bundle', error);
            }
          }
        }
      });
    });

    return bundles;
  }

  // Initialize immediately if DOM is ready, otherwise wait
  if (document.readyState !== 'loading') {
    initBundleDiscounts();
  } else {
    document.addEventListener('DOMContentLoaded', initBundleDiscounts);
  }

  // Re-initialize on dynamic content updates (e.g., theme sections)
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', function(event) {
      if (event.detail.sectionId) {
        setTimeout(initBundleDiscounts, 100);
      }
    });
  }

  // Also listen for theme's page loaded event if available
  document.addEventListener('page:loaded', initBundleDiscounts);

})();

